'use client';

import { css } from '@repo/pandacss/styled-system/css';
import { useId, type CSSProperties, type ReactNode } from 'react';
import { MAX_EXPANDED_HEIGHT } from './constants';
import { useCustomAboveSectionsToggleHandlers } from './useCustomAboveSectionsToggleHandlers';

type ReadMoreToggleProps = {
  aboveReadMoreContent: ReactNode;
  belowReadMoreContent?: ReactNode;
};

export function ReadMoreToggle({
  aboveReadMoreContent,
  belowReadMoreContent,
}: ReadMoreToggleProps) {
  const idSuffix = useId();

  const { handleReadMoreClick, handleReadMoreKeyDown, handleReadLessClick, handleReadLessKeyDown } =
    useCustomAboveSectionsToggleHandlers(idSuffix);

  return (
    <div
      className={css({
        '& a, & .MuiLink-root': {
          color: 'lime.tintLP2',
        },
        '& .checkbox-toggle:checked ~ .first-content .read-more-label': {
          display: 'none',
        },
        '& details > summary:hover': {
          backgroundColor: 'transparent',
          outlineStyle: 'none',
        },
        // Always render remaining content but use max-height to hide when collapsed
        // This keeps content in layout flow to prevent layout shift
        '& .remaining-content': {
          display: 'block',
          maxHeight: '0',
          overflowX: 'hidden',
          overflowY: 'hidden',
          visibility: 'hidden',
          marginTop: '0',
        },
        '& .checkbox-toggle:checked ~ .remaining-content': {
          maxHeight: 'var(--max-expanded-height)',
          overflowX: 'visible',
          overflowY: 'visible',
          visibility: 'visible',
          marginTop: '7',
        },
        // Always render read less button but use max-height to hide when collapsed
        '& .read-less-container': {
          display: 'flex',
          maxHeight: '0',
          overflowX: 'hidden',
          overflowY: 'hidden',
          visibility: 'hidden',
          maxWidth: 'fit',
          marginTop: '0',
          marginRight: '0',
          marginBottom: '0',
          marginLeft: '0',
          paddingTop: '0',
          paddingRight: '0',
          paddingBottom: '0',
          paddingLeft: '0',
        },
        '& .checkbox-toggle:checked ~ .read-less-container': {
          maxHeight: 'var(--max-expanded-height)',
          overflowX: 'visible',
          overflowY: 'visible',
          visibility: 'visible',
          marginTop: '6',
        },
      })}
      style={{ '--max-expanded-height': MAX_EXPANDED_HEIGHT } as CSSProperties}
    >
      <input
        type="checkbox"
        className="checkbox-toggle"
        id={`checkbox-toggle-${idSuffix}`}
        defaultChecked={false}
        hidden
      />
      <span
        className={
          css({
            display: 'inline',
            fontSize: '[inherit]',
            lineHeight: '[inherit]',
            '& *': {
              display: 'inline',
              fontSize: '[inherit]',
              lineHeight: '[inherit]',
            },
          }) + ' first-content'
        }
      >
        {aboveReadMoreContent}

        {belowReadMoreContent && (
          <label
            className={
              css({
                display: 'inline-flex',
                maxWidth: 'fit',
                verticalAlign: 'baseline',
                marginLeft: '1',
              }) + ' read-more-label'
            }
            htmlFor={`checkbox-toggle-${idSuffix}`}
            aria-controls={`remaining-content-${idSuffix}`}
            tabIndex={0}
            onClick={handleReadMoreClick}
            onKeyDown={handleReadMoreKeyDown}
            role="button"
            aria-expanded={false}
          >
            <span
              className={
                css({
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  color: 'lime.tintLP2',
                  alignItems: 'center',
                  maxWidth: '[inherit]',
                  columnGap: '1',
                  rowGap: '1',
                  _hover: {
                    textDecorationLine: 'underline',
                    textDecorationColor: 'lime.tintLP2',
                  },
                }) + ' read-more'
              }
              style={{ fontSize: '.813rem', lineHeight: '1.4', display: 'inline-flex' }}
            >
              Read More
            </span>
          </label>
        )}
      </span>

      {belowReadMoreContent && (
        <>
          <div className="remaining-content" id={`remaining-content-${idSuffix}`}>
            {belowReadMoreContent}
          </div>

          <label
            className={
              css({
                fontWeight: 'bold',
                cursor: 'pointer',
                color: 'lime.tintLP2',
                alignItems: 'center',
                display: 'inline-flex',
                maxWidth: '[inherit]',
                columnGap: '1',
                rowGap: '1',
                fontSize: 'md',
                lineHeight: '[24px]',
                _hover: {
                  textDecorationLine: 'underline',
                  textDecorationColor: 'lime.tintLP2',
                },
              }) + ' read-less-container'
            }
            htmlFor={`checkbox-toggle-${idSuffix}`}
            aria-controls={`remaining-content-${idSuffix}`}
            tabIndex={0}
            onClick={handleReadLessClick}
            onKeyDown={handleReadLessKeyDown}
            aria-expanded={true}
            role="button"
          >
            Show Less
          </label>
        </>
      )}
    </div>
  );
}
